/**
 * `<plp-video>` — обёртка над YouTube/Vimeo.
 *
 * Lite-Embed pattern: до клика рендерим только превью + play-кнопку, iframe
 * подгружается лениво при клике через video-чанк. Это экономит сотни КБ на
 * странице с встроенным видео.
 */

import { loadChunk } from '../core/lazy.js';
import { t } from '../i18n/index.js';

export interface VideoRuntime {
  embed(host: PlpVideo, opts: PlpVideoOptions): void;
  resolvePreview(opts: PlpVideoOptions): Promise<string | null>;
}

export interface PlpVideoOptions {
  provider: 'youtube' | 'vimeo';
  id: string;
  preview: string;
  autoplay: boolean;
}

declare global {
  interface Window {
    __plpRuntime?: { video?: VideoRuntime } & Record<string, unknown>;
  }
}

export class PlpVideo extends HTMLElement {
  static get observedAttributes(): string[] {
    return ['provider', 'id', 'preview', 'autoplay'];
  }

  private _initialized = false;
  private _embedded = false;

  connectedCallback(): void {
    if (this._initialized) return;
    this._initialized = true;
    this.style.display = this.style.display || 'block';
    this.style.position = this.style.position || 'relative';
    if (!this.style.aspectRatio) this.style.aspectRatio = '16 / 9';
    this.renderPreview();
  }

  attributeChangedCallback(): void {
    if (this._initialized && this.isConnected && !this._embedded) this.renderPreview();
  }

  private options(): PlpVideoOptions {
    const provider = (this.getAttribute('provider') ?? 'youtube').toLowerCase();
    return {
      provider: provider === 'vimeo' ? 'vimeo' : 'youtube',
      id: this.getAttribute('id') ?? '',
      preview: this.getAttribute('preview') ?? 'auto',
      autoplay: this.hasAttribute('autoplay'),
    };
  }

  private renderPreview(): void {
    const opts = this.options();
    if (!opts.id) return;

    let img = this.querySelector<HTMLImageElement>(':scope > .plp-video-preview');
    if (!img) {
      img = document.createElement('img');
      img.className = 'plp-video-preview';
      img.alt = '';
      img.loading = 'lazy';
      Object.assign(img.style, {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
        cursor: 'pointer',
      });
      this.appendChild(img);
    }
    img.src = this.previewUrl(opts);

    if (!this.querySelector(':scope > .plp-video-play')) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'plp-video-play';
      btn.setAttribute('aria-label', t('video.play'));
      Object.assign(btn.style, {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '68px',
        height: '48px',
        background: 'rgba(0,0,0,0.65)',
        color: 'white',
        border: 'none',
        borderRadius: '14px',
        cursor: 'pointer',
        fontSize: '24px',
      });
      btn.textContent = '▶';
      this.appendChild(btn);
    }

    this.addEventListener('click', this.onClick);

    // Vimeo требует API-вызов для preview — просим чанк дотянуть превью lazy.
    if (opts.preview === 'auto' && opts.provider === 'vimeo') {
      void this.lazyResolvePreview(opts, img);
    }
  }

  private previewUrl(opts: PlpVideoOptions): string {
    if (opts.preview && opts.preview !== 'auto') return opts.preview;
    if (opts.provider === 'youtube') {
      return `https://i.ytimg.com/vi/${encodeURIComponent(opts.id)}/hqdefault.jpg`;
    }
    return '';
  }

  private async lazyResolvePreview(opts: PlpVideoOptions, img: HTMLImageElement): Promise<void> {
    try {
      await loadChunk('video');
      const runtime = window.__plpRuntime?.video;
      if (!runtime) return;
      const url = await runtime.resolvePreview(opts);
      if (url) img.src = url;
    } catch {
      /* ignore */
    }
  }

  private onClick = async (): Promise<void> => {
    if (this._embedded) return;
    const opts = this.options();
    if (!opts.id) return;
    this._embedded = true;
    try {
      await loadChunk('video');
      const runtime = window.__plpRuntime?.video;
      if (!runtime) throw new Error('video runtime missing');
      runtime.embed(this, { ...opts, autoplay: true });
    } catch {
      this._embedded = false;
    }
  };
}
